import { theme } from '@Guidelines/Theme/theme';
import type { AutomaticSourceDraft } from '@UI/Pages/Administration/DocumentGroups/DocumentGroupEdit/FormData/WorkflowFormData';
import useWorkflowDraft from '@UI/Pages/Administration/DocumentGroups/DocumentGroupEdit/Providers/WorkflowDraftProvider/useWorkflowDraft';
import PillChoice from '@UI/Pages/Administration/DocumentGroups/DocumentGroupEdit/Shared/PillChoice';
import { css } from '@emotion/react';
import * as React from 'react';
import { useSelector } from '@verclam/synapse';
import { Button } from '@verclam/ui-kit';

const rowStyle = css`
	display: flex;
	flex-direction: column;
	gap: 0.75rem;
	padding: 1rem;
	border-radius: ${theme.radii.medium};
	background-color: ${theme.colors.surfaceMuted};
`;

const headerStyle = css`
	display: flex;
	align-items: center;
	justify-content: space-between;
	gap: 0.625rem;
	flex-wrap: wrap;
`;

const titleStyle = css`
	margin: 0;
	font-family: ${theme.fonts.display};
	font-size: 0.9375rem;
	font-weight: ${theme.fontWeights.semibold};
	line-height: 1.3;
	color: ${theme.colors.ink};
`;

type CriterionValue = 'last' | 'verified';

const CRITERIA = [
	{ label: 'Le dernier', value: 'last' },
	{ label: 'Le dernier vérifié', value: 'verified' },
] as const;

type Props = {
	readonly source: AutomaticSourceDraft;
	/** Rank among the taken-over documents, for the title. */
	readonly number: number;
};

/** One taken-over document: its type, whether "the last" or "the last verified" one, and its removal. */
const AutomaticSourceRow: React.FC<Props> = ({ source, number }) => {
	const { draft, setDraft } = useWorkflowDraft();
	const documentTypes = useSelector((state) => state.documentTypesStore.documentTypes);

	const typeOptions = React.useMemo(
		() =>
			(documentTypes?.activeTypes() ?? [])
				.filter(
					(documentType) =>
						documentType.id === source.documentTypeId ||
						!draft.automaticDocumentTypeIds.includes(documentType.id),
				)
				.map((documentType) => ({ label: documentType.label, value: documentType.id })),
		[documentTypes, draft.automaticDocumentTypeIds, source.documentTypeId],
	);

	const handleTypeChange = React.useCallback(
		(documentTypeId: number) => {
			setDraft((current) => current.changeAutomaticSourceType(source.documentTypeId, documentTypeId));
		},
		[setDraft, source.documentTypeId],
	);

	const handleCriterionChange = React.useCallback(
		(value: CriterionValue) => {
			setDraft((current) =>
				current.setAutomaticSourceVerifiedOnly(source.documentTypeId, value === 'verified'),
			);
		},
		[setDraft, source.documentTypeId],
	);

	const handleRemoveClick = React.useCallback(() => {
		setDraft((current) => current.removeAutomaticSource(source.documentTypeId));
	}, [setDraft, source.documentTypeId]);

	return (
		<div css={rowStyle}>
			<div css={headerStyle}>
				<h3 css={titleStyle}>Document repris {number}</h3>
				<Button
					type="button"
					size="sm"
					severity="danger"
					outlined
					label="Retirer"
					onClick={handleRemoveClick}
				/>
			</div>
			<PillChoice
				name={`sourceType-${source.documentTypeId}`}
				label="Type de document"
				options={typeOptions}
				value={source.documentTypeId}
				onChange={handleTypeChange}
			/>
			<PillChoice
				name={`sourceCriterion-${source.documentTypeId}`}
				label="Critère"
				options={CRITERIA}
				value={source.verifiedOnly ? 'verified' : 'last'}
				onChange={handleCriterionChange}
			/>
		</div>
	);
};

export default AutomaticSourceRow;
